import React from 'react';
import styled from 'styled-components';

// [변경 사유]
// - 초기 기획 의도에 맞춰 "Top% + 최대할인 + 근거지표 요약"을 우선 노출합니다.
// - 기존 SalesTriggerGauge(게이지 UI)는 유지하되, 본 컴포넌트가 대시보드 기본 표현을 담당합니다.

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Hero = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #edf2f7;
  background: #f7fafc;
`;

const Left = styled.div`
  min-width: 0;
`;

const Big = styled.div`
  font-size: 1.6rem;
  font-weight: 900;
  color: #1a202c;
`;

const Small = styled.div`
  margin-top: 6px;
  color: #4a5568;
  font-size: 0.9rem;
`;

const Right = styled.div`
  flex: 0 0 auto;
  text-align: right;
`;

const Discount = styled.div`
  font-size: 1.4rem;
  font-weight: 900;
  color: #2b6cb0;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  overflow: hidden;
`;

const Chip = styled.div`
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #2d3748;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
`;

const SubSectionTitle = styled.div`
  margin-top: 2px;
  margin-bottom: 8px;
  /* [변경 사유]
   * - Pre-Check의 "상세 리스트 (Top 10)" 타이틀 기본색(#2c5282)과 톤을 맞춰,
   *   섹션 타이틀 간 시각적 일관성을 확보합니다.
   */
  color: #2c5282;
  font-size: 0.9rem;
  font-weight: 800;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #edf2f7;
  background: #fff;
`;

const ItemLeft = styled.div`
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
  color: #1a202c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemSub = styled.div`
  margin-top: 2px;
  font-size: 0.8rem;
  color: #718096;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemRight = styled.div`
  flex: 0 0 auto;
  text-align: right;
`;

const ItemValue = styled.div`
  font-size: 0.95rem;
  font-weight: 900;
  color: #2b6cb0;
`;

const Empty = styled.div`
  color: #718096;
  font-size: 0.9rem;
`;

const _fmt = (v) => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n % 1 === 0 ? String(n) : n.toFixed(1);
};

const HealthGradeSummary = ({ data }) => {
  if (!data) return <Empty>건강 등급 데이터가 없습니다.</Empty>;

  // [변경 사유]
  // - consultation 응답에 health_grade_v2가 추가되면 v2를 우선 사용합니다.
  // - health_grade_v2가 없으면 기존 sales_trigger 스키마로 폴백합니다.
  const isV2 = !!(data && typeof data === 'object' && data.score && data.grade);

  const topPercent = isV2 ? Math.round(((data?.grade?.percentile_estimate ?? 0) * 100)) : (data?.top_percent ?? 0);
  const grade = isV2 ? (data?.grade?.value ?? 0) : (data?.health_grade ?? 0);

  // [변경 사유]
  // - v2는 보험사별 예측 할인율이 다르므로, 요약 헤더에서는 "최대"만 우선 표시합니다.
  const expectedDiscount = isV2
    ? Math.max(0, ...((data?.discount_predictions || []).map((p) => Number(p?.estimated_discount_percent || 0))))
    : (data?.expected_discount ?? 0);

  const legacyMetrics = data?.metrics || {};
  const v2KeyMetrics = Array.isArray(data?.evidence?.key_metrics) ? data.evidence.key_metrics : [];
  const v2DynamicReasons = Array.isArray(data?.evidence?.dynamic_reasons) ? data.evidence.dynamic_reasons : [];
  const v2Predictions = Array.isArray(data?.discount_predictions) ? data.discount_predictions : [];

  const _findKeyMetric = (key) => {
    const found = v2KeyMetrics.find((m) => String(m?.key || '') === key);
    return found || null;
  };

  const kmBmi = isV2 ? _findKeyMetric('bmi') : null;
  const kmBp = isV2 ? _findKeyMetric('bp_sys') : null;
  const kmGlu = isV2 ? _findKeyMetric('glucose') : null;
  const kmCho = isV2 ? _findKeyMetric('cholesterol') : null;
  const kmAlt = isV2 ? _findKeyMetric('alt') : null;
  const kmCr = isV2 ? _findKeyMetric('creatinine') : null;

  return (
    <Container>
      <Hero>
        <Left>
          <Big>{topPercent ? `Top ${topPercent}%` : '등급 산출 중'}</Big>
          <Small>{grade ? `예상 건강 등급: ${grade}등급` : '검진/의료이용 데이터 기반으로 산출합니다.'}</Small>
        </Left>
        <Right>
          <Discount>{expectedDiscount ? `최대 ${expectedDiscount}%` : '할인 산출 중'}</Discount>
          <Small style={{ marginTop: '2px' }}>예상 할인율</Small>
        </Right>
      </Hero>

      <div>
        <SubSectionTitle>근거 지표 요약</SubSectionTitle>
        <ChipRow>
          <Chip>{`체질량지수: ${_fmt(isV2 ? kmBmi?.value : legacyMetrics?.bmi)}`}</Chip>
          <Chip>{`혈압(최고): ${_fmt(isV2 ? kmBp?.value : legacyMetrics?.bp)}`}</Chip>
          <Chip>{`공복혈당: ${_fmt(isV2 ? kmGlu?.value : legacyMetrics?.glucose)}`}</Chip>
          <Chip>{`총콜레스테롤: ${_fmt(isV2 ? kmCho?.value : legacyMetrics?.cholesterol)}`}</Chip>
          <Chip>{`ALT: ${_fmt(isV2 ? kmAlt?.value : legacyMetrics?.alt)}`}</Chip>
          <Chip>{`크레아티닌: ${_fmt(isV2 ? kmCr?.value : legacyMetrics?.creatinine)}`}</Chip>
        </ChipRow>
      </div>

      {isV2 && v2Predictions.length > 0 ? (
        <div>
          <SubSectionTitle>보험사별 예상 할인</SubSectionTitle>
          <List>
            {v2Predictions.slice(0, 3).map((p, idx) => (
              <ListItem key={idx}>
                <ItemLeft>
                  <ItemTitle>{p?.insurer || '보험사'}</ItemTitle>
                  <ItemSub>{`적용 등급: ${p?.grade_min}~${p?.grade_max} · 재산정: ${p?.recalculation_cycle || '-'}`}</ItemSub>
                </ItemLeft>
                <ItemRight>
                  <ItemValue>{`${_fmt(p?.estimated_discount_percent)}%`}</ItemValue>
                  <ItemSub>{`최대 ${_fmt(p?.discount_max_percent)}%`}</ItemSub>
                </ItemRight>
              </ListItem>
            ))}
          </List>
        </div>
      ) : null}

      {isV2 && v2DynamicReasons.length > 0 ? (
        <div>
          <SubSectionTitle>추가 반영 요인</SubSectionTitle>
          <ChipRow>
            {v2DynamicReasons.slice(0, 3).map((r, idx) => (
              <Chip key={idx}>{String(r)}</Chip>
            ))}
          </ChipRow>
        </div>
      ) : null}
    </Container>
  );
};

export default HealthGradeSummary;
